import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLiveRefresh } from '../../hooks/useLiveRefresh';
import {
    getBookingsByPoojari,
    getEarningsSummary
} from '../../services/bookingService';
import { getBankDetails } from '../../services/poojariService';
import { getUserRating } from '../../services/reviewService';
import { getUserById } from '../../services/userService';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { formatPaise } from '../../utils/money';
import NotificationToggle from '../../components/NotificationToggle';

const RED = '#B22222';
const DARK_RED = '#8B0000';
const GOLD = '#FFD700';

const ACTIVE_STATUSES = ['PENDING', 'CONFIRMED'];

const normalizeList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== 'object') return [];
    const keys = ['data', 'content', 'result', 'items'];
    for (const k of keys) {
        if (Array.isArray(payload[k])) return payload[k];
    }
    return [];
};

// "2026-08-04" + "10:00:00" se Date object
const toDateTime = (b) => {
    if (!b.poojaDate) return new Date(8640000000000000);
    return new Date(
        `${b.poojaDate}T${b.poojaTime || '00:00:00'}`);
};

// Aaj, Kal ya date
const dayLabel = (dateStr) => {
    if (!dateStr) return '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);

    const diff = Math.round(
        (d - today) / (1000 * 60 * 60 * 24));

    if (diff === 0) return 'TODAY';
    if (diff === 1) return 'TOMORROW';
    if (diff < 0) return 'OVERDUE';

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May',
        'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${d.getDate()} ${months[d.getMonth()]}`;
};

// "14:30:00" se "2:30 pm"
const time12h = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':');
    const hour = parseInt(h);
    const period = hour >= 12 ? 'pm' : 'am';
    const h12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${h12}:${m} ${period}`;
};

const PoojariDashboard = () => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userRatings, setUserRatings] = useState({});
    const [userNames, setUserNames] = useState({});

    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        confirmed: 0,
        completed: 0
    });

        const [earnings, setEarnings] = useState({
        received: 0,
        pending: 0,
        pendingCount: 0,
        // ⚠️ Commission breakdown - "View details" ke liye
        totalEarned: 0,
        totalPlatformFee: 0,
        netPayout: 0,
        pendingFee: 0,
        cashBlocked: false
    });

    // Details khuli hai ya nahi. Default band - dashboard saaf
    // rahe aur poojari ko roz commission ka reminder na mile.
    const [showEarningsDetails, setShowEarningsDetails] = useState(false);
    const [bankDetailsMissing, setBankDetailsMissing] = useState(false);

    const { token, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) {
            navigate('/poojari-login');
            return;
        }
        const timer = setTimeout(() => {
            fetchBookings();
        }, 300);
        return () => clearTimeout(timer);
    }, []);

    const fetchBookings = async () => {
        try {
            const poojariId = user?.id;
            if (!poojariId) {
                toast.error('Session expired. Login again.');
                navigate('/poojari-login');
                return;
            }

            const raw = await getBookingsByPoojari(poojariId);
            const data = normalizeList(raw);

            const completed = data.filter(b =>
                b.bookingStatus === 'COMPLETED');

            setStats({
                total: data.length,
                pending: data.filter(b =>
                    b.bookingStatus === 'PENDING').length,
                confirmed: data.filter(b =>
                    b.bookingStatus === 'CONFIRMED').length,
                completed: completed.length
            });

            // Earnings backend se - received aur pending
            try {
                const e = await getEarningsSummary(poojariId);
                    setEarnings({
                    received: e.received || 0,
                    pending: e.pending || 0,
                    pendingCount: e.pendingCount || 0,
                    freePeriod: e.freePeriod,
                    freeRemaining: e.freeRemaining,
                    totalEarned: e.totalEarned || 0,
                    totalPlatformFee: e.totalPlatformFee || 0,
                    netPayout: e.netPayout || 0,
                    pendingFee: e.pendingFee || 0,
                    cashBlocked: e.cashBlocked || false
                });
            } catch (err) {
                console.log('Could not load earnings');
            }
            // ⚠️ Bank details check - ALAG try-catch mein.
            //
            // Ye fail ho to dashboard ka baaki data phir bhi aana
            // chahiye. Ek saath rakhte to bank wali call fail hone
            // pe poora dashboard khaali dikhta.
            try {
                const bank = await getBankDetails(poojariId);
                setBankDetailsMissing(!bank?.hasBankDetails);
            } catch (e) {
                setBankDetailsMissing(true);
            }

            // Sirf active bookings - date time se sorted
            const active = data
                .filter(b => ACTIVE_STATUSES.includes(
                    b.bookingStatus))
                .sort((a, b) => toDateTime(a) - toDateTime(b));

            setBookings(active);
            loadUserInfo(active);

        } catch (error) {
            toast.error('Error fetching bookings');
            setBookings([]);
        } finally {
            setLoading(false);
        }
    };
    useLiveRefresh(fetchBookings);

    // Har user ki rating aur naam
    const loadUserInfo = async (list) => {
        const ids = [...new Set(list.map(b => b.userId))];

        for (const id of ids) {
            try {
                const [rating, info] = await Promise.all([
                    getUserRating(id),
                    getUserById(id)
                ]);
                setUserRatings(prev => ({
                    ...prev, [id]: rating
                }));
                setUserNames(prev => ({
                    ...prev,
                    [id]: `${info.firstName} ${info.lastName}`
                }));
            } catch (err) {
                console.log('Could not load user', id);
            }
        }
    };

    const statusColor = (status) => {
        const map = {
            PENDING: { bg: '#FFF4E0', text: '#8A5300' },
            CONFIRMED: { bg: '#E6F1FB', text: '#0C447C' }
        };
        return map[status] || { bg: '#EFEFEF', text: '#444' };
    };

    // Booking pe click - Manage Bookings pe le jao
    const openBooking = (b) => {
        navigate('/poojari-bookings', {
            state: { focusId: b.id, filter: b.bookingStatus }
        });
    };

    if (loading) return (
        <div className="text-center py-5">
            <div className="spinner-border"
                style={{ color: RED }}></div>
        </div>
    );

    const statCards = [
        {
            label: 'Pending',
            value: stats.pending,
            icon: '⏳',
            bg: '#BA7517'
        },
        {
            label: 'Confirmed',
            value: stats.confirmed,
            icon: '✅',
            bg: '#185FA5'
        },
        {
            label: 'Completed',
            value: stats.completed,
            icon: '🎉',
            bg: '#3B6D11'
        },
        {
            label: 'Total',
            value: stats.total,
            icon: '📅',
            bg: DARK_RED
        }
    ];

    return (
        <div style={{ backgroundColor: '#FFF5F5' }}
            className="pb-screen">

            {(stats.pending > 0 || earnings.pendingCount > 0) && (
                <div style={{
                    backgroundColor: '#FFF8E1',
                    borderBottom: '1px solid #FFB74D',
                    padding: '9px 16px'
                }}>
                    <p className="mb-0 fw-bold"
                        style={{
                            color: '#8A5300',
                            fontSize: '13px'
                        }}>
                        {stats.pending > 0
                            ? `${stats.pending} booking(s) waiting for you`
                            : `${earnings.pendingCount} payment(s) to collect`}
                    </p>
                </div>
            )}

            <div className="container"
                style={{ maxWidth: '640px' }}>
                <div className="py-3">

                  <NotificationToggle role="POOJARI" />
                                      {/* ⚠️ Bank details ka reminder - sirf jab tak bhare
                        na ho.

                        Bina iske poojari ko pata hi nahi chalega ki ye
                        option hai. Profile page pe hai, par wahan wo
                        roz nahi jaata.

                        ⚠️ Bhar dene ke baad ye GAYAB ho jaata hai -
                        kaam ho chuka uska card roz dikhana bekaar hai.

                        ⚠️ "Not needed for cash" likhna zaroori hai.
                        Abhi free period mein sab cash hai, aur bina
                        bataye poojari sochega ki bina bank details ke
                        booking hi nahi milegi - aur ghabra ke chhod
                        dega. */}
                    {bankDetailsMissing && (
                        <div className="p-3 mb-3"
                            style={{
                                backgroundColor: '#FFF8E1',
                                border: '1px solid #FFB74D',
                                borderRadius: '12px'
                            }}>
                            <p className="mb-1 fw-bold"
                                style={{
                                    color: '#8A5300',
                                    fontSize: '13px'
                                }}>
                                Bank account not added
                            </p>
                            <p className="mb-2"
                                style={{
                                    color: '#666',
                                    fontSize: '12px'
                                }}>
                                Add it to receive online payments later.
                                Not needed for cash bookings.
                            </p>
                            <button
                                type="button"
                                className="btn btn-sm fw-bold"
                                style={{
                                    backgroundColor: RED,
                                    color: GOLD,
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '12px'
                                }}
                                onClick={() =>
                                    navigate('/poojari-profile')}>
                                Add now
                            </button>
                        </div>
                    )}
                    {/* ===== STATS ===== */}
                    <div className="row g-2 mb-3">
                        {statCards.map((s, i) => (
                            <div key={i} className="col-3">
                                <div className="text-center py-3"
                                    style={{
                                        backgroundColor: s.bg,
                                        borderRadius: '12px'
                                    }}>
                                    <div style={{
                                        fontSize: '20px'
                                    }}>
                                        {s.icon}
                                    </div>
                                    <div className="fw-bold"
                                        style={{
                                            color: GOLD,
                                            fontSize: '20px',
                                            lineHeight: 1.2
                                        }}>
                                        {s.value}
                                    </div>
                                    <div style={{
                                        color: '#FFE4B5',
                                        fontSize: '11px'
                                    }}>
                                        {s.label}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ===== EARNINGS ===== */}
                    <div className="card mb-3"
                        style={{
                            borderRadius: '12px',
                            border: `1px solid ${GOLD}`
                        }}>
                        <div className="card-body p-3">
                            {/* ⚠️ FREE PERIOD ka message.
                                Poojari ko saaf pata hona chahiye ki
                                pehli 10 poojas ka paisa cash mein
                                milega, app se nahi. Warna wo intezaar
                                karta rahega ki paisa account mein aaye. */}
                            {earnings.freePeriod && (
                                <div className="p-3 mb-3"
                                    style={{
                                        backgroundColor: '#FFF8E1',
                                        border: `1px solid ${GOLD}`,
                                        borderRadius: '8px'
                                    }}>
                                    <p className="mb-1 fw-bold"
                                        style={{
                                            fontSize: '14px',
                                            color: DARK_RED
                                        }}>
                                        💵 Cash in hand for your first 10 poojas
                                    </p>
                                    <small className="text-muted">
                                        For your first 10 completed poojas, the
                                        devotee pays you directly in cash at the
                                        pooja venue. No platform fee, nothing
                                        deducted &mdash; you keep the full amount.
                                        {earnings.freeRemaining > 0 && (
                                            <>
                                                {' '}
                                                <strong>
                                                    {earnings.freeRemaining} more
                                                    {earnings.freeRemaining === 1
                                                        ? ' pooja' : ' poojas'} to go.
                                                </strong>
                                            </>
                                        )}
                                    </small>
                                </div>
                            )}
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-0"
                                        style={{
                                            fontSize: '14px',
                                            color: DARK_RED,
                                            fontWeight: 600
                                        }}>
                                        Received
                                    </p>
                                    <small className="text-muted">
                                        Money in your hand
                                    </small>
                                </div>
                                <h4 className="fw-bold mb-0"
                                    style={{ color: '#3B6D11' }}>
                                    {formatPaise(earnings.received)}
                                </h4>
                            </div>
                                                        {/* ⚠️ Commission breakdown - band rehta hai by default.
                                
                                Dashboard pe roz "fee kati" dikhana demoralizing hai.
                                Par CHHUPANA bhi galat hai - poojari khud jodne
                                baithega aur hisaab alag hone pe shak badhega.
                                Isliye ek layer neeche: dhoondhega to mil jaayega,
                                roz saamne nahi aayega. */}
                            {earnings.totalEarned > 0 && (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 mt-2"
                                        style={{
                                            color: RED,
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            textDecoration: 'none'
                                        }}
                                        onClick={() =>
                                            setShowEarningsDetails(
                                                !showEarningsDetails)}>
                                        {showEarningsDetails
                                            ? 'Hide details'
                                            : 'View earnings details'}
                                    </button>

                                    {showEarningsDetails && (
                                        <div className="mt-2 pt-2"
                                            style={{
                                                borderTop: `1px solid ${GOLD}`
                                            }}>
                                            <div className="d-flex justify-content-between"
                                                style={{ fontSize: '13px' }}>
                                                <span className="text-muted">
                                                    Total earned
                                                </span>
                                                <span>
                                                    {formatPaise(
                                                        earnings.totalEarned)}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between mt-1"
                                                style={{ fontSize: '13px' }}>
                                                <span className="text-muted">
                                                    Platform fee
                                                </span>
                                                <span style={{ color: '#8A5300' }}>
                                                    {earnings.totalPlatformFee > 0
                                                        ? `- ${formatPaise(earnings.totalPlatformFee)}`
                                                        : 'No fee yet'}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between mt-2 pt-2"
                                                style={{
                                                    fontSize: '14px',
                                                    borderTop: '1px solid #EEE'
                                                }}>
                                                <strong style={{ color: DARK_RED }}>
                                                    Net earnings
                                                </strong>
                                                <strong className="text-success">
                                                    {formatPaise(
                                                        earnings.netPayout)}
                                                </strong>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {earnings.pending > 0 && (
                                <>
                                    <hr className="my-3" />
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <p className="mb-0"
                                                style={{
                                                    fontSize: '14px',
                                                    color: '#791F1F',
                                                    fontWeight: 600
                                                }}>
                                                Pending
                                            </p>
                                            <small className="text-muted">
                                                From {earnings.pendingCount}{' '}
                                                completed pooja(s)
                                            </small>
                                        </div>
                                        <h4 className="fw-bold mb-0"
                                            style={{
                                                color: '#791F1F'
                                            }}>
                                            {formatPaise(earnings.pending)}
                                        </h4>
                                    </div>

                                    <button
                                        className="btn btn-sm w-100 mt-3 fw-bold"
                                        style={{
                                            backgroundColor: GOLD,
                                            color: DARK_RED,
                                            border: 'none'
                                        }}
                                        onClick={() =>
                                            navigate(
                                                '/poojari-bookings',
                                                { state: {
                                                    filter: 'COMPLETED'
                                                }})}>
                                        Collect pending payments
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                    
                    {/* ⚠️ BAKAYA - cash bookings se banta hai.
                        Un mein platform fee banti to hai par katti nahi:
                        paisa poojari ke haath seedha jaata hai.

                        Upar card mein "Platform fee" kati hui dikhti hai.
                        Ye box batata hai ki usme se kitna abhi bhi dena
                        baaki hai.

                        ⚠️ Ye card ke ANDAR nahi, BAAD mein hai - jaan-boojh
                        ke. Andar daalne se wo "Net earnings" wali line ke
                        daayein chipak jaata tha. */}
                    {earnings.pendingFee > 0 && (
                        <div className="p-3 mb-4"
                            style={{
                                backgroundColor: '#FFF8E1',
                                border: '1px solid #FFD54F',
                                borderRadius: '12px'
                            }}>
                            <div className="d-flex justify-content-between align-items-center">
                                <span style={{
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#8D6E00'
                                }}>
                                    Pending fee
                                </span>
                                <span style={{
                                    fontSize: '16px',
                                    fontWeight: 700,
                                    color: '#8D6E00'
                                }}>
                                    {formatPaise(earnings.pendingFee)}
                                </span>
                            </div>
                            <div style={{
                                fontSize: '12px',
                                color: '#8D6E00',
                                marginTop: '4px'
                            }}>
                                {earnings.cashBlocked
                                    ? 'Cash bookings are paused until this is cleared.'
                                    : 'Will be adjusted from your next online booking.'}
                            </div>
                        </div>
                    )}

                    {/* ===== QUICK LINKS ===== */}
                    <div className="row g-2 mb-4">
                        <div className="col-6">
                            <button
                                className="btn w-100 fw-bold py-2"
                                style={{
                                    backgroundColor: RED,
                                    color: GOLD,
                                    borderRadius: '10px',
                                    border: 'none',
                                    fontSize: '14px'
                                }}
                                onClick={() =>
                                    navigate('/poojari-bookings')}>
                                📅 All Bookings
                            </button>
                        </div>
                        <div className="col-6">
                            <button
                                className="btn w-100 fw-bold py-2"
                                style={{
                                    backgroundColor: GOLD,
                                    color: DARK_RED,
                                    borderRadius: '10px',
                                    border: 'none',
                                    fontSize: '14px'
                                }}
                                onClick={() =>
                                    navigate('/poojari-profile')}>
                                👤 My Profile
                            </button>
                        </div>
                    </div>

                    {/* ===== UPCOMING ===== */}
                    <h6 className="fw-bold mb-1"
                        style={{ color: DARK_RED }}>
                        Upcoming poojas
                    </h6>
                    <p className="text-muted mb-3"
                        style={{ fontSize: '13px' }}>
                        Nearest first · tap to see details
                    </p>

                    {bookings.length === 0 ? (
                        <div className="text-center py-5">
                            <h6 style={{ color: DARK_RED }}>
                                No upcoming poojas
                            </h6>
                            <p className="text-muted"
                                style={{ fontSize: '14px' }}>
                                New booking requests will
                                appear here
                            </p>
                        </div>
                    ) : (
                        <div className="row g-2">
                            {bookings.map(b => {
                                const sc = statusColor(
                                    b.bookingStatus);
                                const label = dayLabel(
                                    b.poojaDate);
                                const isSoon =
                                    label === 'TODAY'
                                    || label === 'TOMORROW'
                                    || label === 'OVERDUE';
                                const rating =
                                    userRatings[b.userId];

                                return (
                                    <div key={b.id}
                                        className="col-12">
                                        <div
                                            onClick={() =>
                                                openBooking(b)}
                                            className="card"
                                            style={{
                                                borderRadius: '12px',
                                                cursor: 'pointer',
                                                border: isSoon
                                                    ? `2px solid ${RED}`
                                                    : `1px solid ${GOLD}`
                                            }}>
                                            <div className="card-body p-3">

                                                <div className="d-flex align-items-center gap-3">

                                                    {/* Date box */}
                                                    <div className="text-center flex-shrink-0"
                                                        style={{
                                                            minWidth: '70px'
                                                        }}>
                                                        <div style={{
                                                            fontSize: '10px',
                                                            fontWeight: 700,
                                                            color: isSoon
                                                                ? RED
                                                                : '#999',
                                                            letterSpacing:
                                                                '0.5px'
                                                        }}>
                                                            {label}
                                                        </div>
                                                        <div style={{
                                                            fontSize: '14px',
                                                            fontWeight: 600,
                                                            color: DARK_RED
                                                        }}>
                                                            {time12h(
                                                                b.poojaTime)}
                                                        </div>
                                                    </div>

                                                    {/* Details */}
                                                    <div className="flex-grow-1"
                                                        style={{
                                                            minWidth: 0
                                                        }}>
                                                        <p className="mb-0 fw-bold text-truncate"
                                                            style={{
                                                                color: DARK_RED,
                                                                fontSize: '14px'
                                                            }}>
                                                            {b.poojaType}
                                                        </p>
                                                        <p className="mb-0 text-muted text-truncate"
                                                            style={{
                                                                fontSize: '12px'
                                                            }}>
                                                            {userNames[b.userId]
                                                                || 'Devotee'}
                                                            {rating > 0
                                                                && ` · ⭐ ${rating}`}
                                                        </p>
                                                        <p className="mb-0 text-muted text-truncate"
                                                            style={{
                                                                fontSize: '12px'
                                                            }}>
                                                            📍 {b.poojaCity}
                                                            {b.poojaPincode
                                                                && ` - ${b.poojaPincode}`}
                                                        </p>
                                                    </div>

                                                    {/* Amount + status */}
                                                    <div className="text-end flex-shrink-0">
                                                        <div className="fw-bold"
                                                            style={{
                                                                color: RED,
                                                                fontSize: '15px'
                                                            }}>
                                                            {formatPaise(b.bookingAmount)}
                                                        </div>
                                                        <span className="badge"
                                                            style={{
                                                                backgroundColor:
                                                                    sc.bg,
                                                                color: sc.text,
                                                                fontSize: '10px'
                                                            }}>
                                                            {b.bookingStatus}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PoojariDashboard;